/**
 * Pure helper functions for Vedic astrology calculations.
 */

import { pymod, pythonRound, zfill } from "../lib/pyutil";
import type { PlanetPosition, RawChartData } from "./models";
import { get_divisional_position as _canonical_get_divisional_position } from "./transit_data";

// Import constants directly to avoid circular imports
export const SIGN_NAMES = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];

export const NAKSHATRA_NAMES = [
  "Ashwini", "Bharani", "Krittika", "Rohini", "Mrigashira", "Ardra",
  "Punarvasu", "Pushya", "Ashlesha", "Magha", "Purva Phalguni", "Uttara Phalguni",
  "Hasta", "Chitra", "Swati", "Vishakha", "Anuradha", "Jyeshtha",
  "Mula", "Purva Ashadha", "Uttara Ashadha", "Shravana", "Dhanishta", "Shatabhisha",
  "Purva Bhadrapada", "Uttara Bhadrapada", "Revati",
];

// Exact nakshatra geometry (13°20' span, 3°20' pada). Mirrors _constants.py;
// defined locally because this module intentionally avoids importing _constants.
export const NAKSHATRA_SPAN_DEG = 360.0 / 27.0;
export const NAKSHATRA_PADA_DEG = 360.0 / 108.0;

// Nakshatra lords in order (Ketu starts Ashwini)
export const NAKSHATRA_LORDS = [
  "ketu", "venus", "sun", "moon", "mars", "rahu", "jupiter", "saturn", "mercury",
  "ketu", "venus", "sun", "moon", "mars", "rahu", "jupiter", "saturn", "mercury",
  "ketu", "venus", "sun", "moon", "mars", "rahu", "jupiter", "saturn", "mercury",
];

// Dignity tables live in constants.ts — the SINGLE source of truth (the 2026-07-02 cleanup
// removed this file's byte-identical duplicates after the Moon-Moolatrikona fix had to be made
// in both copies; re-exported here so every existing `from "./helpers"` import keeps working).
export { EXALTATIONS, DEBILITATIONS, OWN_SIGNS, MOOLATRIKONA } from "./constants";
import { EXALTATIONS, DEBILITATIONS, OWN_SIGNS, MOOLATRIKONA } from "./constants";

/**
 * Deep-exaltation DEGREE within the exaltation sign (BPHS Ch.27 / Vol_02 §4.2). EXALTATIONS
 * carries only the sign; this carries the exact point at which exaltation strength
 * (Uccha Bala) peaks. The deep-debilitation point is exactly 180° away. The nodes' deep
 * degree is classically disputed, so they have no entry — sign-level dignity only
 * (see positional_dignity_strength).
 */
export const EXALTATION_DEGREE: Record<string, number> = {
  sun: 10.0, moon: 3.0, mars: 28.0, mercury: 15.0,
  jupiter: 5.0, venus: 27.0, saturn: 20.0,
};

// Dig Bala (directional strength) - planets strong in specific houses
// House numbers where planet gets full dig bala
export const DIG_BALA_HOUSES: Record<string, number> = {
  sun: 10, // Strong in 10th (zenith)
  mars: 10, // Strong in 10th
  saturn: 7, // Strong in 7th (west)
  jupiter: 1, // Strong in 1st (east)
  mercury: 1, // Strong in 1st
  venus: 4, // Strong in 4th (nadir)
  moon: 4, // Strong in 4th
};

// Average daily motion for cheshta bala calculation
export const MEAN_DAILY_MOTION: Record<string, number> = {
  sun: 0.9856,
  moon: 13.1764,
  mercury: 1.3833,
  venus: 1.2,
  mars: 0.524,
  jupiter: 0.0831,
  saturn: 0.0335,
  rahu: 0.0529,
  ketu: 0.0529,
};

/** Normalize longitude to 0-360 degrees. */
export function norm_deg(x: number): number {
  return pymod(pymod(x, 360) + 360, 360);
}

/** Get nakshatra number, name, and pada from sidereal longitude.
 *
 * INTENTIONAL CPython-parity quirk (do not "fix"): at exact boundary longitudes (e.g. 40.0)
 * the division-based index and the fmod-based position can disagree, yielding an incoherent
 * index/position pair. This mirrors the Python source exactly and is fixture-locked; the same
 * quirk lives in chart.get_nakshatra and dasha.init_dasha_jd. See pyfloat.ts. */
export function get_nakshatra(longitude: number): [number, string, number] {
  const lon = norm_deg(longitude);
  const nak_num = pymod(Math.trunc(lon / NAKSHATRA_SPAN_DEG), 27);
  const pos_in_nak = pymod(lon, NAKSHATRA_SPAN_DEG);
  const pada = Math.min(Math.trunc(pos_in_nak / NAKSHATRA_PADA_DEG) + 1, 4);
  return [nak_num, NAKSHATRA_NAMES[nak_num], pada];
}

/** Get the nakshatra lord for a given longitude. */
export function get_nakshatra_lord(longitude: number): string {
  const nak_num = pymod(Math.trunc(norm_deg(longitude) / NAKSHATRA_SPAN_DEG), 27);
  return NAKSHATRA_LORDS[nak_num];
}

/** Derive sign information from sidereal longitude. */
export function get_sign_from_longitude(longitude: number): [number, string, number] {
  const lon = norm_deg(longitude);
  const sign = Math.trunc(Math.floor(lon / 30));
  const degree_in_sign = pymod(lon, 30);
  return [sign, SIGN_NAMES[sign], degree_in_sign];
}

/** Calculate house number (1-12) from planet sign and ascendant sign. */
export function get_house_from_signs(planet_sign: number, asc_sign: number): number {
  return pymod(planet_sign - asc_sign, 12) + 1;
}

/** Convert decimal longitude to degrees°minutes′ within sign. */
export function dec_to_dms(longitude: number): string {
  const lon = norm_deg(longitude);
  const sign_pos = pymod(lon, 30);
  let deg = Math.trunc(sign_pos);
  let minutes = Math.trunc(pythonRound((sign_pos - deg) * 60));
  if (minutes === 60) {
    deg += 1;
    minutes = 0;
  }
  if (deg === 30) {
    deg = 0;
  }
  return `${deg}°${zfill(minutes, 2)}′`;
}

/**
 * Compute planetary dignity from sign placement (degree-aware when the in-sign degree is given).
 * Returns (dignity_name, strength_score 0.0-1.0); values keep the classical ordering
 * exalted (1.0) > moolatrikona (0.9) > own (0.85) > neutral (0.5) > debilitated (0.15).
 *
 * Moolatrikona is resolved by the explicit DEGREE zones of 02_planetary_dignities.md
 * "Mooltrikona Zones" (e.g. "Sun: 0°–20° Leo (Mooltrikona); 20°–30° Leo (own sign only)",
 * "Mercury: 15°–20° Virgo (Mooltrikona); 0°–15° Virgo (exaltation); 20°–30° Virgo (own sign)",
 * "Moon: >3°–30° Taurus (Mooltrikona); 0°–3° Taurus (exaltation portion)") — checked BEFORE
 * plain own-sign, which previously made the MT tier unreachable for every classical planet.
 *
 * Callers that only know the sign (no degree) get the sign's DOMINANT tier: the exaltation
 * overlap wins where it exists (Moon/Taurus, Mercury/Virgo — their exaltation zones start at
 * 0°), otherwise the MT zone labels the sign.
 *
 * For the continuous positional strength used by ShadbalaLite see
 * positional_dignity_strength(). This is kept for callers that need the dignity NAME.
 */
export function compute_dignity(planet: string, sign: number, degree_in_sign?: number): [string, number] {
  const p = planet.toLowerCase();

  if (p in MOOLATRIKONA && sign === MOOLATRIKONA[p][0]) {
    const [, mt_start, mt_end] = MOOLATRIKONA[p];
    const is_exalt_sign = p in EXALTATIONS && EXALTATIONS[p] === sign;
    if (degree_in_sign === undefined) {
      return is_exalt_sign ? ["exalted", 1.0] : ["moolatrikona", 0.9];
    }
    if (mt_start <= degree_in_sign && degree_in_sign < mt_end) {
      return ["moolatrikona", 0.9];
    }
    if (is_exalt_sign && degree_in_sign < mt_start) {
      // Below the MT zone start = the exaltation portion (Moon 0–3° Taurus, Mercury 0–15° Virgo).
      return ["exalted", 1.0];
    }
    if (p in OWN_SIGNS && OWN_SIGNS[p].includes(sign)) {
      // Above the MT zone = own-sign-only portion (Mercury 20–30° Virgo, Sun 20–30° Leo, …).
      return ["own", 0.85];
    }
    // MT sign that is neither own nor below-MT exaltation at this degree (no current table
    // entry reaches this — kept for safety).
    return is_exalt_sign ? ["exalted", 1.0] : ["neutral", 0.5];
  }

  if (p in EXALTATIONS && sign === EXALTATIONS[p]) {
    return ["exalted", 1.0];
  } else if (p in DEBILITATIONS && sign === DEBILITATIONS[p]) {
    return ["debilitated", 0.15];
  } else if (p in OWN_SIGNS && OWN_SIGNS[p].includes(sign)) {
    return ["own", 0.85];
  }

  return ["neutral", 0.5];
}

/**
 * Natural planetary friendship (02_planetary_relationships.md "Natural Friendship Table").
 * Fixed, permanent, chart-independent. The Moon has NO natural enemies — consistent across
 * the classical tradition. Rahu/Ketu have no row in the classical table; 02_rahu_ketu_rules.md
 * makes the nodes dispositor-dominant instead, so natural_relation() treats them as NEUTRAL
 * (both as the subject planet and as a sign lord — the 7-graha SIGN_RULERS scheme never
 * yields a node dispositor anyway).
 */
export const NATURAL_FRIENDS: Record<string, string[]> = {
  sun: ["moon", "mars", "jupiter"],
  moon: ["sun", "mercury"],
  mars: ["sun", "moon", "jupiter"],
  mercury: ["sun", "venus"],
  jupiter: ["sun", "moon", "mars"],
  venus: ["mercury", "saturn"],
  saturn: ["mercury", "venus"],
};

export const NATURAL_ENEMIES: Record<string, string[]> = {
  sun: ["venus", "saturn"],
  moon: [], // Moon has no natural enemies
  mars: ["mercury"],
  mercury: ["moon"],
  jupiter: ["mercury", "venus"],
  venus: ["sun", "moon"],
  saturn: ["sun", "moon", "mars"],
};

/**
 * Natural relation of `planet` toward `other`: "friend" | "neutral" | "enemy".
 * Unknown bodies (nodes, angles) and the self-relation come back "neutral".
 */
export function natural_relation(planet: string, other: string): string {
  const p = (planet || "").toLowerCase();
  const o = (other || "").toLowerCase();
  if (!(p in NATURAL_FRIENDS) || p === o) {
    return "neutral";
  }
  if (NATURAL_FRIENDS[p].includes(o)) {
    return "friend";
  }
  if (NATURAL_ENEMIES[p].includes(o)) {
    return "enemy";
  }
  return "neutral";
}

/**
 * Temporary (chart-specific) relation of `planet` toward `other`:
 * 02_planetary_relationships.md "Temporary (Chart-Specific) Friendship":
 *   "Temporary Friend: planet is in the 2nd, 3rd, 4th, 10th, 11th, or 12th house FROM another
 *    planet — Temporary Enemy: planet is in the 1st, 5th, 6th, 7th, 8th, or 9th house FROM
 *    another planet."
 * Houses are whole-sign counts from the subject planet's sign. Returns "friend" | "enemy",
 * or "neutral" when either body is missing from the chart or for the self-relation (the
 * compound table then falls back to the natural relation alone).
 */
const _TEMP_FRIEND_HOUSES = new Set([2, 3, 4, 10, 11, 12]);

export function temporary_relation(chart: RawChartData, planet: string, other: string): string {
  const p = (planet || "").toLowerCase();
  const o = (other || "").toLowerCase();
  const ppos = chart.planets[p];
  const opos = chart.planets[o];
  if (p === o || ppos === undefined || opos === undefined) {
    return "neutral";
  }
  const house = get_house_from_signs(opos.sign, ppos.sign);
  return _TEMP_FRIEND_HOUSES.has(house) ? "friend" : "enemy";
}

/**
 * Compound (combined) relation of `planet` toward `other` — natural + temporary per the
 * 02_planetary_relationships.md "Compound (Combined) Planetary Relationship" table:
 *   Friend+Friend = Great Friend; Friend+Enemy = Neutral; Neutral+Friend = Friend;
 *   Neutral+Enemy = Enemy; Enemy+Friend = Neutral; Enemy+Enemy = Great Enemy;
 *   Natural × (temporary) Neutral keeps the natural tier.
 * "This is the definitive relationship used for interpretation."
 * Returns "great_friend" | "friend" | "neutral" | "enemy" | "great_enemy".
 */
export function compound_relation(chart: RawChartData, planet: string, other: string): string {
  const nat = natural_relation(planet, other);
  const tmp = temporary_relation(chart, planet, other);
  if (tmp === "neutral") {
    // Degenerate temporary relation (missing positions / self): the doc's Natural×Neutral rows.
    return nat;
  }
  if (nat === "friend") {
    return tmp === "friend" ? "great_friend" : "neutral";
  }
  if (nat === "enemy") {
    return tmp === "friend" ? "neutral" : "great_enemy";
  }
  return tmp === "friend" ? "friend" : "enemy";
}

/**
 * Vargottama: the body occupies the SAME sign in D-1 and D-9 (02_planetary_dignities.md
 * "Vargottama"; 05_strength_assessment_thresholds.md). Pure function of the sidereal
 * longitude — the D-9 sign comes from the canonical varga engine, so this stays in exact
 * agreement with every other navamsa consumer.
 */
export function is_vargottama(longitude: number): boolean {
  const lon = norm_deg(longitude);
  const d1_sign = Math.trunc(Math.floor(lon / 30));
  const [d9_sign] = _canonical_get_divisional_position(lon, 9);
  return d1_sign === d9_sign;
}

/**
 * Absolute sidereal longitude (0-360) of a planet's deep-exaltation point, or None
 * for bodies without one (nodes, luminary-less). Vol_02 §4.2 / BPHS Ch.27.
 */
export function deep_exaltation_longitude(planet: string): number | null {
  const p = planet.toLowerCase();
  if (!(p in EXALTATIONS) || !(p in EXALTATION_DEGREE)) {
    return null;
  }
  return EXALTATIONS[p] * 30.0 + EXALTATION_DEGREE[p];
}

/**
 * Degree-aware exaltation strength (Uccha Bala), normalised 0.0-1.0 (BPHS Ch.27).
 *
 * 1.0 at the deep-exaltation degree, 0.0 at the deep-debilitation point 180° opposite,
 * 0.5 at 90° from either — varying CONTINUOUSLY with the exact longitude. This is what the
 * sign-only dignity misses: two planets in the same sign but at different degrees deliver
 * differently, and a planet one degree inside its exaltation sign is far weaker than one
 * sitting on the deep-exaltation point. Returns 0.5 (neutral) for bodies with no
 * exaltation point.
 */
export function compute_uccha_bala(planet: string, longitude: number): number {
  const deep_exalt = deep_exaltation_longitude(planet);
  if (deep_exalt === null) {
    return 0.5;
  }
  const deep_debil = pymod(deep_exalt + 180.0, 360.0);
  let sep = Math.abs(norm_deg(longitude) - deep_debil);
  if (sep > 180.0) {
    sep = 360.0 - sep;
  }
  return Math.max(0.0, Math.min(1.0, sep / 180.0));
}

/**
 * Degree-aware positional strength 0.0-1.0 for ShadbalaLite.
 *
 * Base = the continuous Uccha Bala (exaltation gradient by exact degree). Own-sign and
 * Moolatrikona then FLOOR the score, because a planet in its own rāśi is dignified
 * regardless of how far it sits from the exaltation degree (e.g. Saturn in Aquarius is
 * strong even though Saturn's deep exaltation is in Libra). Debilitation needs no special
 * case — it falls out of the Uccha gradient as ~0 near the deep-debilitation point.
 */
export function positional_dignity_strength(planet: string, longitude: number, sign: number): number {
  const p = planet.toLowerCase();
  let score = compute_uccha_bala(p, longitude);
  const deg_in_sign = pymod(norm_deg(longitude), 30.0);
  // Bodies with no deep-exaltation DEGREE (the nodes — Uccha Bala returns a flat 0.5 for them)
  // take SIGN-level exaltation/debilitation instead of the continuous gradient.
  if (deep_exaltation_longitude(p) === null && p in EXALTATIONS) {
    if (sign === EXALTATIONS[p]) score = Math.max(score, 0.85);
    else if (sign === DEBILITATIONS[p]) score = Math.min(score, 0.15);
  }
  if (p in MOOLATRIKONA) {
    const [mt_sign, mt_start, mt_end] = MOOLATRIKONA[p];
    if (sign === mt_sign && mt_start <= deg_in_sign && deg_in_sign < mt_end) {
      score = Math.max(score, 0.9);
    } else if ((OWN_SIGNS[p] ?? []).includes(sign)) {
      score = Math.max(score, 0.85);
    }
  } else if (p in OWN_SIGNS && OWN_SIGNS[p].includes(sign)) {
    score = Math.max(score, 0.85);
  }
  return Math.max(0.0, Math.min(1.0, score));
}

/**
 * Compute directional strength (dig bala).
 * Returns 0.0-1.0 based on house placement.
 * Full strength in optimal house, zero in opposite.
 */
export function compute_dig_bala(planet: string, house: number): number {
  const p = planet.toLowerCase();
  if (!(p in DIG_BALA_HOUSES)) {
    return 0.5; // Nodes, outer planets get neutral
  }

  const optimal_house = DIG_BALA_HOUSES[p];
  const opposite_house = pymod(optimal_house - 1 + 6, 12) + 1;

  if (house === optimal_house) {
    return 1.0;
  } else if (house === opposite_house) {
    return 0.0;
  } else {
    // Linear interpolation based on house distance from optimal
    const distance = Math.min(Math.abs(house - optimal_house), 12 - Math.abs(house - optimal_house));
    return Math.max(0.0, 1.0 - distance / 6.0);
  }
}

/**
 * Raw Paksha Bala strength 0..1 (05_shadbala_kaal_bala.md "Kaal Bala — Paksha Bala"):
 * "Subtract Sun's longitude from Moon's longitude; if over 180°, subtract from 360°;
 * divide by 3 = Paksha Bala" (of 60 Virupas) → phase-arc/180 normalised: 0 at New Moon,
 * 1 at Full Moon. Unrounded, so second-by-second candidate charts stay continuous
 * (compute_moon_paksha rounds the same value to 3 dp for display).
 */
export function moon_paksha_strength(sun_longitude: number, moon_longitude: number): number {
  const phase_angle = norm_deg(moon_longitude - sun_longitude); // 0..360
  const strength = 1.0 - Math.abs(phase_angle - 180.0) / 180.0;
  return Math.max(0.0, Math.min(1.0, strength));
}

const _DEG = Math.PI / 180.0;
// Mean obliquity of the ecliptic (23°26'13" ≈ 23.437°) — the "23°27'" of the Ayan Bala
// formula in 05_shadbala_kaal_bala.md.
const _OBLIQUITY_DEG = 23.437;

/** Extra chart context for the luminaries' Cheshta Bala (see compute_cheshta_bala). */
export interface CheshtaContext {
  /** The planet's own sidereal longitude (needed for Sun and Moon). */
  longitude?: number;
  /** The Sun's sidereal longitude (needed for the Moon's Paksha Bala). */
  sun_longitude?: number;
  /** The chart ayanamsa (needed for the Sun's Ayan Bala: tropical = sidereal + ayanamsa). */
  ayanamsa?: number;
}

/**
 * Compute motional strength (Cheshta Bala), normalised 0..1 (÷60 Virupas).
 *
 * 02_planetary_strengths.md: Cheshta Bala comes "from planetary motion (retrograde = strong;
 * fast = weak)". 05_shadbala_chesht_naisargika_drik_bala.md 8-motion table (Mars–Saturn):
 * Vakra (retrograde) 60, Manda (slower than mean) 30, Vikala (stationary) 15,
 * Sama (slightly faster than mean) 7.5 Virupas.
 *
 *   - Five true planets: retrograde → 1.0 (60/60); stationary (|speed| ≲ 0) → 0.25 (15/60);
 *     direct motion maps SLOWER-than-mean to stronger — 0.5 (Manda 30/60) at ≤half the mean
 *     speed, easing linearly to 0.125 (Sama 7.5/60) at ≥1.5× the mean speed.
 *   - Moon: "Moon's Chesht Bala = Moon's Paksha Bala" (same doc) — the waxing/bright-half
 *     fraction 0..1 via moon_paksha_strength (waxing Moon stronger, full Moon = 1).
 *   - Sun: "Sun's Chesht Bala = Sun's Ayan Bala" — declination strength: with
 *     sin(decl) = sin(23.437°)·sin(tropical longitude), ayan = (24 + decl)/48, so northern
 *     declination is stronger (range ~0.01..0.99; the 05_shadbala_kaal_bala.md formula
 *     60·(23°27′ + decl)/46°54′ normalised to 0..1 with a rounded 24° half-range).
 *   - Rahu/Ketu: perpetually retrograde → the Vakra tier (1.0). Their Shadbala barely feeds
 *     any consumer, but the constant keeps the 8-motion table's convention.
 *   - Unknown bodies (outer planets, angles) → neutral 0.5.
 *
 * The luminaries need chart context (`ctx`); without it they fall back to neutral 0.5.
 */
export function compute_cheshta_bala(
  planet: string,
  speed: number,
  is_retrograde: boolean,
  ctx: CheshtaContext = {}
): number {
  const p = planet.toLowerCase();

  if (p === "sun") {
    if (ctx.longitude === undefined || ctx.ayanamsa === undefined) {
      return 0.5;
    }
    const tropical = norm_deg(ctx.longitude + ctx.ayanamsa);
    const decl_deg = Math.asin(Math.sin(_OBLIQUITY_DEG * _DEG) * Math.sin(tropical * _DEG)) / _DEG;
    return Math.max(0.0, Math.min(1.0, (24.0 + decl_deg) / 48.0));
  }

  if (p === "moon") {
    if (ctx.longitude === undefined || ctx.sun_longitude === undefined) {
      return 0.5;
    }
    return moon_paksha_strength(ctx.sun_longitude, ctx.longitude);
  }

  if (p === "rahu" || p === "ketu") {
    return 1.0; // always retrograde → Vakra (60 Virupas)
  }

  if (!(p in MEAN_DAILY_MOTION)) {
    return 0.5;
  }

  // Vakra: retrograde = maximum strength (60/60).
  if (is_retrograde || speed < 0) {
    return 1.0;
  }

  const mean_speed = MEAN_DAILY_MOTION[p];
  const actual_speed = Math.abs(speed);

  // Vikala: stationary (no apparent motion) = 15/60.
  if (actual_speed < mean_speed * 0.1) {
    return 0.25;
  }

  // Direct motion: slower than typical is STRONGER (Manda 30/60), faster is weaker
  // (Sama 7.5/60); linear between half and 1.5× the planet's mean speed.
  const ratio = actual_speed / mean_speed;
  if (ratio <= 0.5) {
    return 0.5;
  }
  if (ratio >= 1.5) {
    return 0.125;
  }
  return 0.5 - (ratio - 0.5) * 0.375;
}

/**
 * Compute bhava madhya (house centers) from house cusps.
 * The center of each house is midway between its cusp and next cusp.
 */
export function compute_bhava_centers(house_cusps: number[]): number[] {
  const centers: number[] = [];
  for (let i = 0; i < 12; i++) {
    const cusp = house_cusps[i];
    let next_cusp = house_cusps[(i + 1) % 12];

    // Handle wraparound at 360/0
    if (next_cusp < cusp) {
      next_cusp += 360;
    }

    const center = norm_deg((cusp + next_cusp) / 2.0);
    centers.push(center);
  }

  return centers;
}

/**
 * Compute strength factor based on distance from bhava center.
 * Planets closer to house center are stronger in that house's significations.
 * Returns 0.5-1.0 (center=1.0, edge=0.5)
 */
export function compute_bhava_distance_factor(
  planet_longitude: number,
  house: number,
  bhava_centers: number[],
  house_cusps: number[]
): number {
  if (!bhava_centers.length || house < 1 || house > 12) {
    return 0.75;
  }

  const center = bhava_centers[house - 1];
  const cusp = house_cusps[house - 1];
  let next_cusp = house_cusps[house % 12];

  // Calculate house span
  if (next_cusp < cusp) {
    next_cusp += 360;
  }
  const house_span = next_cusp - cusp;

  // Distance from center (normalized to house span)
  const lon = norm_deg(planet_longitude);
  let dist_from_center = Math.abs(norm_deg(lon - center));
  if (dist_from_center > 180) {
    dist_from_center = 360 - dist_from_center;
  }

  // Normalize to 0-1 (0 = at center, 1 = at edge)
  const half_span = house_span / 2.0;
  let normalized_dist: number;
  if (half_span > 0) {
    normalized_dist = Math.min(1.0, dist_from_center / half_span);
  } else {
    normalized_dist = 0.5;
  }

  // Convert to strength factor (center=1.0, edge=0.5)
  return 1.0 - normalized_dist * 0.5;
}

/** Get the houses a planet aspects via Graha Drishti. */
export function get_graha_drishti_houses(planet: string, config_drishti: Record<string, number[]>): number[] {
  const p = planet.toLowerCase();
  if (p in config_drishti) {
    return config_drishti[p];
  }
  return config_drishti["default"] ?? [7];
}

/**
 * Aspect strength for a house distance (BPHS Ch. 26 graha drishti).
 *
 * Special FULL aspects: Mars 4/7/8, Jupiter 5/7/9, Saturn 3/7/10 (02_planetary_aspects.md /
 * 05_aspects_drishti.md). The nodes get no special aspects — the source assigns them none and
 * classical BPHS lists drishti for the seven grahas only (matching the transit layer, which also
 * treats Rahu/Ketu as 7th-only).
 *
 * Partial strengths are the classical BPHS Ch. 26 grades: quarter on 3rd/10th, HALF on 5th/9th,
 * THREE-QUARTERS on 4th/8th, full on 7th. The updated source (02_planetary_aspects.md,
 * 05_aspects_drishti.md) now carries exactly these corrected values (4/8 = ¾, 5/9 = ½).
 */
export function get_drishti_strength(house_distance: number, planet: string): [boolean, number] {
  const full_aspects: Record<string, number[]> = {
    mars: [4, 7, 8],
    jupiter: [5, 7, 9],
    saturn: [3, 7, 10],
  };

  const p = planet.toLowerCase();

  if (house_distance === 7) {
    return [true, 1.0];
  }

  if (p in full_aspects && full_aspects[p].includes(house_distance)) {
    return [true, 1.0];
  }

  const partial_strengths: Record<number, number> = { 3: 0.25, 4: 0.75, 5: 0.5, 8: 0.75, 9: 0.5, 10: 0.25 };

  if (house_distance in partial_strengths) {
    return [false, partial_strengths[house_distance]];
  }

  return [false, 0.0];
}

/** Calculate divisional chart position using the canonical varga engine. */
export function get_divisional_position(lon: number, division: number): [number, number] {
  return _canonical_get_divisional_position(lon, division);
}

/**
 * Compute functional benefic/malefic roles based on lagna (ascendant sign).
 * Returns dict with roles and yogakarakas.
 *
 * Rules:
 * - Lords of kendras (1,4,7,10) become neutral (even natural benefics)
 * - Lords of trikonas (1,5,9) are benefic
 * - Lords of dusthanas (6,8,12) are malefic
 * - Lord of both kendra + trikona = yogakaraka
 * - Lords of 3,11 are mild malefics
 */
export function compute_functional_roles(
  asc_sign: number,
  rulerships: Record<number, string>
): { roles: Record<string, number>; yogakarakas: string[] } {
  // FunctionalRole values are inlined (-1/0/1/2) to avoid a circular import with models;
  // Python late-imports FunctionalRole from ._models for the same reason.
  const MALEFIC = -1;
  const NEUTRAL = 0;
  const BENEFIC = 1;
  const YOGAKARAKA = 2;

  // House lordships for this ascendant
  const house_lords: Record<string, number[]> = {};
  for (let house = 1; house <= 12; house++) {
    const house_sign = pymod(asc_sign + house - 1, 12);
    const lord = rulerships[house_sign] ?? "sun";
    if (!(lord in house_lords)) {
      house_lords[lord] = [];
    }
    house_lords[lord].push(house);
  }

  const roles: Record<string, number> = {};
  const yogakarakas: string[] = [];

  const kendras = new Set([1, 4, 7, 10]);
  const trikonas = new Set([1, 5, 9]);
  const dusthanas = new Set([6, 8, 12]);
  const mild_malefic_houses = new Set([3, 11]);
  // const upachaya = new Set([3, 6, 10, 11]);  // declared but unused in Python too

  for (const [planet, houses] of Object.entries(house_lords)) {
    const houses_set = new Set(houses);

    // Check for yogakaraka (rules both kendra and trikona, excluding 1st)
    const kendra_lords = [...houses_set].filter((h) => kendras.has(h) && h !== 1);
    const trikona_lords = [...houses_set].filter((h) => trikonas.has(h) && h !== 1);

    if (kendra_lords.length && trikona_lords.length) {
      roles[planet] = YOGAKARAKA;
      yogakarakas.push(planet);
      continue;
    }

    // Calculate score
    let score = 0;

    for (const h of houses) {
      if (trikonas.has(h)) {
        score += 2;
      } else if (kendras.has(h)) {
        score += 0; // Kendradhipati dosha - neutralized
      } else if (dusthanas.has(h)) {
        score -= 2;
      } else if (mild_malefic_houses.has(h)) {
        score -= 1;
      }
    }

    if (score >= 2) {
      roles[planet] = BENEFIC;
    } else if (score <= -2) {
      roles[planet] = MALEFIC;
    } else {
      roles[planet] = NEUTRAL;
    }
  }

  // Scorpio lagna special case — 02_planetary_relationships.md "Functional Nature by
  // Ascendant: Scorpio Lagna" explicitly names BOTH luminaries: "Yogakaraka: Sun and Moon
  // (both named)" (Moon as 9th lord/trikona, Sun as 10th lord). Each rules a single house,
  // so the generic kendra+trikona derivation above can never produce them; the source's
  // explicit naming is applied directly.
  if (asc_sign === 7) {
    for (const p of ["sun", "moon"]) {
      roles[p] = YOGAKARAKA;
      if (!yogakarakas.includes(p)) {
        yogakarakas.push(p);
      }
    }
  }

  // Nodes are generally malefic unless well-placed
  roles["rahu"] = MALEFIC;
  roles["ketu"] = MALEFIC;

  // Outer planets are neutral
  for (const p of ["uranus", "neptune", "pluto", "chiron"]) {
    roles[p] = NEUTRAL;
  }

  return { roles, yogakarakas };
}

/** Smallest angular separation in degrees (0..180). */
export function _angular_sep_deg(a: number, b: number): number {
  const d = Math.abs(norm_deg(a) - norm_deg(b));
  return d <= 180 ? d : 360 - d;
}

/**
 * Combustion (Asta) orbs per Vol_02 §8.1 — the classical BPHS table. Mercury and Venus
 * have tighter orbs when retrograde. (These previously sat at a uniform 8° for Mars/
 * Jupiter/Saturn with the Moon missing entirely — Mars at 9° from the Sun was wrongly
 * combust while Saturn at 14° wrongly escaped, mis-gating every strength verdict.)
 */
const _COMBUSTION_ORB_DEG: Record<string, number> = {
  moon: 12.0,
  mars: 17.0,
  mercury: 14.0,
  jupiter: 11.0,
  venus: 10.0,
  saturn: 15.0,
};
const _COMBUSTION_ORB_RETRO_DEG: Record<string, number> = {
  mercury: 12.0,
  venus: 8.0,
};

/**
 * Combustion orb (degrees from the Sun) per Vol_02 §8.1, with the retrograde
 * variants for Mercury/Venus. Nodes/outer bodies/angles never combust (0).
 */
export function get_combustion_range_deg(planet: string, is_retrograde = false): number {
  const p = planet.toLowerCase();
  if (is_retrograde && p in _COMBUSTION_ORB_RETRO_DEG) {
    return _COMBUSTION_ORB_RETRO_DEG[p];
  }
  return _COMBUSTION_ORB_DEG[p] ?? 0.0;
}

/**
 * Combustion check: planet within its classical orb of the Sun (Vol_02 §8.1).
 * The orb test is on angular separation, so the full Moon (180° away) is never
 * combust — only the New-Moon proximity weakens it, exactly as the doc notes.
 *
 * Returns:
 *     { "is_combust": bool, "orb_deg": float, "range_deg": float }
 */
export function compute_combustion(
  sun_longitude: number,
  planet_longitude: number,
  planet: string,
  is_retrograde = false
): Record<string, any> {
  const rng = get_combustion_range_deg(planet, is_retrograde);
  const orb = _angular_sep_deg(sun_longitude, planet_longitude);
  return {
    is_combust: Boolean(rng > 0 && orb <= rng),
    orb_deg: pythonRound(orb, 3),
    range_deg: rng,
  };
}

/**
 * Moon paksha (waxing/waning) + a simple strength proxy derived from Sun-Moon phase angle.
 *
 * Strength proxy:
 *   - 0.0 near new moon (0°)
 *   - 1.0 near full moon (180°)
 */
export function compute_moon_paksha(sun_longitude: number, moon_longitude: number): Record<string, any> {
  const phase_angle = norm_deg(moon_longitude - sun_longitude); // 0..360
  const waxing = phase_angle < 180.0;
  // distance from new/full; map to 0..1 where full ~= 1
  const strength = moon_paksha_strength(sun_longitude, moon_longitude);
  const tithi = Math.trunc(Math.floor(phase_angle / 12.0)) + 1; // 1..30

  return {
    phase: waxing ? "waxing" : "waning",
    phase_angle_deg: pythonRound(phase_angle, 3),
    tithi: tithi,
    strength: pythonRound(Math.max(0.0, Math.min(1.0, strength)), 3),
  };
}

/**
 * Basic graha yuddha (planetary war) detector.
 *
 *   - Only classical visible planets: Mercury, Venus, Mars, Jupiter, Saturn
 *     (the Sun combusts instead; the Moon and the nodes never war — 05_shadbala_kaal_bala.md).
 *   - Separation <= 1.0 degree => war (05_shadbala_kaal_bala.md: "two planets within 1° of each
 *     other" — the condition is pure angular separation, so a war straddling a sign cusp counts
 *     too; an earlier same-sign guard silently dropped those — fixed 2026-07-02).
 *   - The VICTOR is the planet BEHIND along the short arc: within one sign this is exactly the
 *     docs' "lower degree within its sign" rule (02_planetary_strengths.md §Planetary War), and
 *     it stays correct across a cusp (29°50′ Virgo beats 0°20′ Libra) where a literal
 *     degree-in-sign comparison would invert the intent.
 */
export function compute_graha_yuddha(planets: Record<string, PlanetPosition>): Array<Record<string, any>> {
  const candidates = ["mercury", "venus", "mars", "jupiter", "saturn"];
  const wars: Array<Record<string, any>> = [];

  for (let i = 0; i < candidates.length; i++) {
    const p1 = candidates[i];
    if (!(p1 in planets)) {
      continue;
    }
    for (let j = i + 1; j < candidates.length; j++) {
      const p2 = candidates[j];
      if (!(p2 in planets)) {
        continue;
      }

      const a = planets[p1];
      const b = planets[p2];
      const sep = _angular_sep_deg(a.longitude, b.longitude);
      if (sep <= 1.0) {
        // p1 wins when it sits behind p2 along the short arc (a tie keeps p1 — deterministic).
        const forward = pymod(b.longitude - a.longitude, 360.0);
        const victor = forward <= 180.0 ? p1 : p2;
        wars.push({
          p1: p1,
          p2: p2,
          // Per-belligerent signs: a cusp-straddling war has TWO signs, so a single `sign`
          // field would mislocate p2 (it used to be valid only under the same-sign guard).
          p1_sign: a.sign,
          p2_sign: b.sign,
          separation_deg: pythonRound(sep, 3),
          victor: victor,
          defeated: victor === p1 ? p2 : p1,
        });
      }
    }
  }

  return wars;
}

/**
 * Collapse compute_dignity() into a simple label for explanations. Pass the in-sign degree
 * when known so the Moolatrikona degree zones resolve exactly (see compute_dignity).
 */
export function dignity_flag(planet: string, sign: number, degree_in_sign?: number): string {
  const [name] = compute_dignity(planet, sign, degree_in_sign);
  return name;
}
